import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import { buildRatioSummary, buildSeriesSummary, fraction, isFiniteNumber } from '@vello/charts-core';
import { ChartEmpty, ChartLoading, ChartTooltip, colorOf, cx, defaultFormatValue, joinSummary, toneColor, useElementWidth, useMotionState, type ChartToneName } from './kit';
import type { ChartBaseProps } from './line-chart';
import { LiveDot, ProgressTrack, type ChartTrackSize } from './progress';

// Bars and the meter are laid out in HTML/CSS rather than SVG: their labels wrap, their values align right in the mono
// face and a long label never collides with the bar. The fill width is a percentage, so they are responsive by nature.

export interface BarListItem {
  readonly key?: string;
  readonly label: string;
  readonly value: number;
  /** Mark a STATE (success / warning / danger); default is the ink series. */
  readonly tone?: ChartToneName;
}

/** A click on a mark of a chart (D528: the cross-filter contract's chart half; `additive` is Shift, Ctrl or Cmd). */
export interface ChartSelect {
  readonly additive: boolean;
}

export interface BarListProps extends ChartBaseProps {
  readonly items: readonly BarListItem[];
  /** The value that fills the track (default: the largest value). */
  readonly max?: number;
  /** Show only the first `limit` rows. */
  readonly limit?: number;
  /**
   * D528: make each row a button. A click (or Enter / Space) calls this with the row and whether Shift, Ctrl or Cmd was held. Without it the
   * list is the plain image it always was.
   */
  readonly onSelect?: (item: BarListItem, index: number, how: ChartSelect) => void;
  /** Keys of the rows to mark as selected (`aria-pressed`), when `onSelect` is given. */
  readonly selectedKeys?: readonly string[];
  /** Track height of every row (default `md`, 8px). */
  readonly size?: ChartTrackSize;
  /** D566: add the row's share of the listed total to its tooltip (off by default: the rows of a list are not always parts of a whole). */
  readonly showShare?: boolean;
  /** Format a share (a fraction 0–1) in the tooltip (default: a whole percent). */
  readonly formatShare?: (fraction: number) => string;
}

/** Horizontal bars, one row each: the label on the left, the value (mono) on the right, the bar under both. */
export function BarList(props: BarListProps): ReactElement {
  if (props.loading === true) return <ChartLoading height={96} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  const items = (props.limit !== undefined ? props.items.slice(0, Math.max(0, props.limit)) : props.items).filter((i) => isFiniteNumber(i.value));
  if (items.length === 0) return <ChartEmpty height={96} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return <BarListRows {...props} items={items} />;
}

function BarListRows(props: BarListProps): ReactElement {
  const format = props.formatValue ?? defaultFormatValue;
  const motion = useMotionState(props.animate);
  const whole = props.max !== undefined && props.max > 0 ? props.max : Math.max(1e-9, ...props.items.map((i) => Math.max(0, i.value)));
  const summary = props.ariaLabel ?? joinSummary([props.title, buildSeriesSummary(props.items.map((i) => ({ label: i.label, value: i.value })), { format })]);
  // D566: hover or keyboard focus on a row makes it active (outlined track, the other rows recede) and opens a tooltip with the label,
  // the value and, on request, the share; `selectedKeys` marks the chosen rows with `data-chosen` in both the plain and the clickable list.
  const listRef = useRef<HTMLUListElement>(null);
  const width = useElementWidth(listRef, 320);
  const [activeIndex, setActive] = useState<number | null>(null);
  const chosenKeys = props.selectedKeys ?? [];
  const chosenOf = (item: BarListItem): { 'data-chosen'?: 'true' | 'false' } => (chosenKeys.length > 0 ? { 'data-chosen': chosenKeys.includes(item.key ?? item.label) ? 'true' : 'false' } : {});
  const total = props.items.reduce((sum, i) => sum + Math.max(0, i.value), 0);
  const formatShare = props.formatShare ?? ((f: number) => `${Math.round(f * 100)}%`);
  const rowProps = (item: BarListItem, i: number) => ({
    ...(activeIndex === null ? {} : { 'data-active': activeIndex === i ? ('true' as const) : ('false' as const) }),
    ...chosenOf(item),
    onPointerEnter: () => setActive(i),
    onPointerLeave: () => setActive((prev) => (prev === i ? null : prev)),
    onFocus: () => setActive(i),
    onBlur: () => setActive((prev) => (prev === i ? null : prev)),
  });
  const tip = (item: BarListItem, i: number): ReactElement | null =>
    activeIndex !== i ? null : (
      <ChartTooltip
        x={fraction(Math.max(0, item.value), whole) * width}
        y={0}
        width={width}
        heading={item.label}
        rows={[
          { key: 'value', value: format(item.value), color: colorOf(0, item.tone) },
          ...(props.showShare === true && total > 0 ? [{ key: 'share', value: formatShare(fraction(Math.max(0, item.value), total)) }] : []),
        ]}
      />
    );
  const rowBody = (item: BarListItem, i: number): ReactElement => (
    <>
      <span className="vello-bar-list__label">{item.label}</span>
      <span className="tnum vello-bar-list__value">{format(item.value)}</span>
      <ProgressTrack fraction={fraction(Math.max(0, item.value), whole)} size={props.size ?? 'md'} tone={item.tone ?? 'neutral'} index={i} />
    </>
  );
  const select = props.onSelect;
  if (select !== undefined) {
    // Clickable rows are real buttons (focusable, Enter / Space, a 48 px hit area), so the list is a list of controls, not one image.
    const selected = new Set(props.selectedKeys ?? []);
    return (
      <ul ref={listRef} className={cx('vello-chart', 'vello-bar-list', 'vello-bar-list--select', props.className)} data-motion={motion} aria-label={summary}>
        {props.items.map((item, i) => (
          <li key={item.key ?? `${item.label}-${i}`} className="vello-bar-list__row vello-bar-list__row--select" data-selectable="true" {...rowProps(item, i)}>
            <button
              type="button"
              className="vello-bar-list__hit"
              aria-pressed={selected.has(item.key ?? item.label)}
              onClick={(e) => select(item, i, { additive: e.shiftKey || e.ctrlKey || e.metaKey })}
            >
              {rowBody(item, i)}
            </button>
            {tip(item, i)}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul ref={listRef} className={cx('vello-chart', 'vello-bar-list', props.className)} data-motion={motion} role="img" aria-label={summary}>
      {props.items.map((item, i) => (
        <li key={item.key ?? `${item.label}-${i}`} className="vello-bar-list__row" {...rowProps(item, i)}>
          {rowBody(item, i)}
          {tip(item, i)}
        </li>
      ))}
    </ul>
  );
}

export interface MeterProps extends ChartBaseProps {
  readonly label: string;
  readonly value: number;
  /** The whole the value is measured against (default 100). */
  readonly max?: number;
  /** From this fraction of the whole the meter turns warning (e.g. 0.8). */
  readonly warnAt?: number;
  /** From this fraction of the whole the meter turns danger (e.g. 1). */
  readonly dangerAt?: number;
  /** Show the whole next to the value, e.g. `40 / 100` (default true). */
  readonly showMax?: boolean;
  /** A localized word for the warning / danger state ("Near the limit", "Over the limit"): shown as text and added to the accessible label, so colour is never the only signal. */
  readonly warnLabel?: string;
  readonly dangerLabel?: string;
  readonly hint?: ReactNode;
  /** Track height: `xs` 4, `sm` 6, `md` 8 (default), `lg` 10. */
  readonly size?: ChartTrackSize;
  /** The bar is progressing right now: FX-06 sweep on the fill and a pulsing live dot beside the label. */
  readonly live?: boolean;
}

/** A progress bar against a limit that turns warning, then danger, at thresholds — and says so in words, not only in colour. */
export function Meter(props: MeterProps): ReactElement {
  if (props.loading === true) return <ChartLoading height={32} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  if (!isFiniteNumber(props.value)) return <ChartEmpty height={32} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return <MeterBody {...props} />;
}

function MeterBody(props: MeterProps): ReactElement {
  const format = props.formatValue ?? defaultFormatValue;
  const motion = useMotionState(props.animate);
  const max = props.max !== undefined && props.max > 0 ? props.max : 100;
  const f = fraction(Math.max(0, props.value), max);
  const raw = Math.max(0, props.value) / max;
  const tone: ChartToneName = props.dangerAt !== undefined && raw >= props.dangerAt ? 'danger' : props.warnAt !== undefined && raw >= props.warnAt ? 'warning' : 'neutral';
  const status = tone === 'danger' ? props.dangerLabel : tone === 'warning' ? props.warnLabel : undefined;
  const summary = props.ariaLabel ?? buildRatioSummary({ label: props.label, value: props.value, max, format, ...(status !== undefined ? { suffix: status } : {}) });
  // D566: hovering the meter outlines its track and opens a tooltip with the label, the value against the whole, the percentage and the state.
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, 320);
  const [hover, setHover] = useState(false);
  return (
    <div ref={ref} className={cx('vello-chart', 'vello-meter', props.className)} data-motion={motion} data-tone={tone} onPointerEnter={() => setHover(true)} onPointerLeave={() => setHover(false)}>
      <div className="vello-meter__head">
        <span className="vello-meter__label">
          {props.live === true && <LiveDot />}
          {props.label}
        </span>
        <span className="vello-meter__figures">
          <span className="tnum vello-meter__value">{format(props.value)}</span>
          {(props.showMax ?? true) && <span className="tnum vello-meter__max">{` / ${format(max)}`}</span>}
        </span>
      </div>
      <ProgressTrack fraction={f} size={props.size ?? 'md'} tone={tone} live={props.live ?? false} role="img" ariaLabel={summary} {...(hover ? { dataActive: true } : {})} />
      {hover && (
        <ChartTooltip
          x={f * width}
          y={0}
          width={width}
          heading={props.label}
          rows={[
            { key: 'value', value: `${format(props.value)} / ${format(max)}`, color: colorOf(0, tone === 'neutral' ? undefined : tone) },
            { key: 'share', value: `${Math.round(raw * 100)}%` },
            ...(status !== undefined ? [{ key: 'status', value: status }] : []),
          ]}
        />
      )}
      {(status !== undefined || props.hint !== undefined) && (
        <p className="vello-meter__foot" style={{ color: tone === 'neutral' ? undefined : toneColor(tone) }}>
          {status ?? props.hint}
        </p>
      )}
    </div>
  );
}
